"use client";
import ColorNextSteps from "@/components/ColorNextSteps";

import {
  Container,
  Title,
  Group,
  Button,
  Stack,
  Text,
  Box,
  ColorSwatch,
  Badge,
  Card,
  SimpleGrid,
  Paper,
} from "@mantine/core";
import { NavigationProgress, setNavigationProgress } from "@mantine/nprogress";
import { IconArrowRight, IconRefresh, IconCircleCheck, IconCircleX } from "@tabler/icons-react";
import { useState, useEffect, useRef } from "react";
import QuizPhotoUpload from "@/components/QuizPhotoUpload";
import UndertoneChecks from "./UndertoneChecks";
import TypeformQuiz, { QuizQuestion } from "@/components/TypeformQuiz";
import { trackQuizStart, trackQuizComplete, trackCtaClick } from "@/lib/analytics";
import EmailResultsCapture from "@/components/EmailResultsCapture";
import { useTranslations, useLocale } from "next-intl";
import { Link } from "@/i18n/navigation";

function getQuizCount(): number {
  const baseCount = 47832;
  const baseDate = new Date("2025-01-01").getTime();
  const now = Date.now();
  const daysSinceBase = Math.floor((now - baseDate) / (1000 * 60 * 60 * 24));
  return baseCount + daysSinceBase * 73;
}

// Questions will be generated using translations in the component

function determineUndertone(answers: Record<string, string>) {
  const values = Object.values(answers);
  const coolAnswers = values.filter((answer) => answer === "cool").length;
  const warmAnswers = values.filter((answer) => answer === "warm").length;
  const neutralAnswers = values.filter((answer) => answer === "neutral").length;

  if (neutralAnswers >= 2) return "neutral";
  if (coolAnswers > warmAnswers) return "cool";
  if (warmAnswers > coolAnswers) return "warm";
  return "neutral";
}

export default function UndertoneQuizContent() {
  const t = useTranslations("undertoneQuiz");
  const locale = useLocale();
  const tQ = useTranslations("undertoneQuiz.questions");
  const tSEO = useTranslations("undertoneQuiz.seo");

  const [quizState, setQuizState] = useState<"intro" | "quiz" | "results">("intro");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showStickyButton, setShowStickyButton] = useState(false);
  const mainCtaRef = useRef<HTMLButtonElement>(null);

  // Generate questions from translations
  const questions: QuizQuestion[] = [
    {
      id: "veins",
      title: tQ("veinTitle"),
      description: tQ("veinDescription"),
      helpText: tQ("veinHelp"),
      options: [
        { label: tQ("veinBlue"), value: "cool" },
        { label: tQ("veinGreen"), value: "warm" },
        { label: tQ("veinBoth"), value: "neutral" },
      ],
    },
    {
      id: "jewelry",
      title: tQ("jewelryTitle"),
      description: tQ("jewelryDescription"),
      helpText: tQ("jewelryHelp"),
      options: [
        { label: tQ("jewelryGold"), value: "warm" },
        { label: tQ("jewelrySilver"), value: "cool" },
        { label: tQ("jewelryBoth"), value: "neutral" },
      ],
    },
    {
      id: "paper",
      title: tQ("paperTitle"),
      description: tQ("paperDescription"),
      helpText: tQ("paperHelp"),
      options: [
        { label: tQ("paperPink"), value: "cool" },
        { label: tQ("paperYellow"), value: "warm" },
        { label: tQ("paperNone"), value: "neutral" },
      ],
    },
    {
      id: "colors",
      title: tQ("colorsTitle"),
      description: tQ("colorsDescription"),
      helpText: tQ("colorsHelp"),
      options: [
        { label: tQ("colorsCool"), value: "cool" },
        { label: tQ("colorsWarm"), value: "warm" },
        { label: tQ("colorsBoth"), value: "neutral" },
      ],
    },
    {
      id: "sun",
      title: tQ("sunTitle"),
      description: tQ("sunDescription"),
      helpText: tQ("sunHelp"),
      options: [
        { label: tQ("sunBurns"), value: "cool" },
        { label: tQ("sunTans"), value: "warm" },
        { label: tQ("sunBoth"), value: "neutral" },
      ],
    },
  ];

  // Show sticky button only after scrolling past the main CTA
  useEffect(() => {
    if (quizState !== "intro") return;

    const handleScroll = () => {
      if (mainCtaRef.current) {
        const rect = mainCtaRef.current.getBoundingClientRect();
        setShowStickyButton(rect.bottom < 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [quizState]);

  const startQuiz = () => {
    setQuizState("quiz");
    setNavigationProgress(0);
    trackQuizStart("Undertone Quiz");
  };

  const handleQuizComplete = (quizAnswers: Record<string, string>) => {
    setAnswers(quizAnswers);
    setQuizState("results");
    setNavigationProgress(100);
    trackQuizComplete("Undertone Quiz");
  };

  const handleQuizExit = () => {
    setQuizState("intro");
    setNavigationProgress(0);
  };

  const resetQuiz = () => {
    setQuizState("intro");
    setAnswers({});
    setNavigationProgress(0);
  };

  // ============================================
  // RESULTS VIEW
  // ============================================
  if (quizState === "results") {
    const undertone = determineUndertone(answers);
    const undertoneData = {
      cool: {
        title: t("results.coolTitle"),
        description: t("results.coolDescription"),
        bestColors: ["#4169E1", "#9370DB", "#DB2777", "#0D9488", "#87CEEB", "#C0C0C0"],
        avoidColors: ["#FF8C00", "#DAA520", "#E9967A", "#808000"],
        season: t("results.coolSeason"),
        jewelry: t("results.coolJewelry"),
      },
      warm: {
        title: t("results.warmTitle"),
        description: t("results.warmDescription"),
        bestColors: ["#E2711D", "#D4A017", "#8B4513", "#556B2F", "#FF6347", "#DAA520"],
        avoidColors: ["#4169E1", "#FF69B4", "#C0C0C0", "#9370DB"],
        season: t("results.warmSeason"),
        jewelry: t("results.warmJewelry"),
      },
      neutral: {
        title: t("results.neutralTitle"),
        description: t("results.neutralDescription"),
        bestColors: ["#0D9488", "#B76E79", "#6B7280", "#D97706", "#4C6A92", "#A78B6F"],
        avoidColors: ["#FF1493", "#39FF14", "#FF4500", "#00FFFF"],
        season: t("results.neutralSeason"),
        jewelry: t("results.neutralJewelry"),
      },
    };

    const data = undertoneData[undertone as keyof typeof undertoneData];

    return (
      <Container size="xl" py="xl">
        <NavigationProgress size={4} color="wumpus" />

        <Stack gap="lg" align="center">
          <Card withBorder p="xl" radius="md" w="100%">
            <Stack gap="lg" align="center">
              <Badge size="lg" color="teal" variant="light">
                {t("results.yourResult")}
              </Badge>
              <Title order={2} c="teal.7" ta="center">
                {data.title}
              </Title>
              <Text ta="center" fw={500} maw={520}>
                {data.description}
              </Text>

              {/* Best colors */}
              <Box w="100%">
                <Group gap="xs" mb={6} justify="center">
                  <IconCircleCheck size={18} color="var(--mantine-color-teal-6)" />
                  <Text fw={600} size="sm" c="teal.7">
                    {t("results.bestColorsLabel")}
                  </Text>
                </Group>
                <Group justify="center" gap="xs">
                  {data.bestColors.map((color, index) => (
                    <ColorSwatch key={index} color={color} size={40} radius="sm" />
                  ))}
                </Group>
              </Box>

              {/* Colors to skip */}
              <Box w="100%">
                <Group gap="xs" mb={6} justify="center">
                  <IconCircleX size={18} color="var(--mantine-color-gray-5)" />
                  <Text fw={600} size="sm" c="dimmed">
                    {t("results.avoidColorsLabel")}
                  </Text>
                </Group>
                <Group justify="center" gap="xs">
                  {data.avoidColors.map((color, index) => (
                    <ColorSwatch
                      key={index}
                      color={color}
                      size={32}
                      radius="sm"
                      style={{ opacity: 0.5 }}
                    />
                  ))}
                </Group>
              </Box>

              {/* Seasons + jewelry facts */}
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" w="100%" mt="xs">
                <Paper withBorder p="md" radius="md" bg="gray.0">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb={4}>
                    {t("results.seasonsLabel")}
                  </Text>
                  <Text fw={600}>{data.season}</Text>
                </Paper>
                <Paper withBorder p="md" radius="md" bg="gray.0">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb={4}>
                    {t("results.jewelryLabel")}
                  </Text>
                  <Text fw={600}>{data.jewelry}</Text>
                </Paper>
              </SimpleGrid>

              {locale === "en" && <Text ta="center" size="sm">Undertone is one part of your palette. <Link href="/color-analysis-quiz" onClick={() => trackCtaClick("undertone_result_color_quiz")}>Take the free color season quiz</Link> to compare warmth, depth and contrast.</Text>}

              {/* Confident next step */}
              <Paper w="100%" p="md" radius="md" bg="wumpus.0" style={{ border: "1px solid var(--mantine-color-wumpus-2)" }}>
                <Text ta="center" size="sm" c="dimmed">
                  {t("results.nextStepText")}
                </Text>
                <Button
                  component={Link}
                  href="/ai-color-analysis"
                  color="wumpus"
                  fullWidth
                  mt="sm"
                  rightSection={<IconArrowRight size={18} />}
                  onClick={() => trackCtaClick("undertone_quiz_result_ai")}
                >
                  {t("results.nextStepCta")}
                </Button>
              </Paper>

              <Paper withBorder w="100%" p="md" radius="md">
                <Text ta="center" fw={600} mb={4}>
                  See it on your own face
                </Text>
                <Text ta="center" size="sm" c="dimmed" mb="sm">
                  You got a result — now prove it. Upload a selfie and our free virtual draping tool wraps you in gold vs silver, cream vs white, and warm vs cool red drapes. Free, no signup.
                </Text>
                <Button
                  component={Link}
                  href="/virtual-color-draping?src=bridge_utq"
                  variant="outline"
                  color="teal"
                  fullWidth
                  rightSection={<IconArrowRight size={18} />}
                  onClick={() => trackCtaClick("bridge_utq_vd")}
                >
                  Drape yourself free
                </Button>
              </Paper>

              <Box w="100%" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }} />

              <QuizPhotoUpload quizName="Undertone Quiz" />

              <Box w="100%">
                <EmailResultsCapture source="undertone-quiz" undertone={undertone} resultLabel={data.title} />
              </Box>
            </Stack>
          </Card>

          <ColorNextSteps source="undertone" />
          <Button variant="subtle" onClick={resetQuiz} leftSection={<IconRefresh size="1rem" />} color="gray" size="sm">
            {t("results.takeAgain")}
          </Button>
        </Stack>
      </Container>
    );
  }

  // ============================================
  // QUIZ VIEW (Typeform-style)
  // ============================================
  if (quizState === "quiz") {
    return (
      <>
        <NavigationProgress size={4} color="wumpus" />
        <TypeformQuiz
          questions={questions}
          onComplete={handleQuizComplete}
          onExit={handleQuizExit}
          accentColor="#12B886" // teal
        />
      </>
    );
  }

  // ============================================
  // INTRO VIEW
  // ============================================
  return (
    <>
      <Container size="xl" py="xl">
        <NavigationProgress size={4} color="wumpus" />

        {/* Above the fold: TOOL, not content */}
        <Stack gap="md" ta="center">
          <Title order={1}>{t("hero.title")}</Title>

          <Text c="dimmed">{t("hero.subtitle")}</Text>

          <Button
            ref={mainCtaRef}
            size="xl"
            onClick={startQuiz}
            rightSection={<IconArrowRight size="1.2rem" />}
            color="wumpus"
            fullWidth
          >
            {t("hero.button")}
          </Button>

          <Text size="xs" c="dimmed">
            {t("hero.noSignup")} · {t("hero.peopleTaken", { count: getQuizCount().toLocaleString() })}
          </Text>
        </Stack>

        {/* Quick check strip: above the fold, answers the query before the quiz card.
            English only — the strings are not translated yet. */}
        {locale === "en" && (
        <Stack gap="xs" mt="lg">
          <Text size="sm" fw={600} ta="center">
            {t("quickCheck.title")}
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xs">
            <Card withBorder p="xs" radius="md">
              <Text size="xs" fw={600} mb={2}>
                {t("quickCheck.veinsTitle")}
              </Text>
              <Text size="xs" c="dimmed">
                {t("quickCheck.veinsText")}
              </Text>
            </Card>
            <Card withBorder p="xs" radius="md">
              <Text size="xs" fw={600} mb={2}>
                {t("quickCheck.jewelryTitle")}
              </Text>
              <Text size="xs" c="dimmed">
                {t("quickCheck.jewelryText")}
              </Text>
            </Card>
            <Card withBorder p="xs" radius="md">
              <Text size="xs" fw={600} mb={2}>
                {t("quickCheck.whiteVsCreamTitle")}
              </Text>
              <Text size="xs" c="dimmed">
                {t("quickCheck.whiteVsCreamText")}
              </Text>
            </Card>
          </SimpleGrid>
          <Text size="xs" c="dimmed" ta="center">
            {t("quickCheck.notSure")}
          </Text>
        </Stack>

        )}

        {/* Intro section */}
        <Stack gap="md" mt="xl">
          <Text>
            {t("intro.description")}
          </Text>
          <Box>
            <Text mb="xs">{t("intro.benefitsTitle")}</Text>
            <Stack gap="xs" ml="md">
              <Text>• {t("intro.benefit1")}</Text>
              <Text>• {t("intro.benefit2")}</Text>
              <Text>• {t("intro.benefit3")}</Text>
              <Text>• {t("intro.benefit4")}</Text>
            </Stack>
          </Box>
        </Stack>

        {locale === "en" && <UndertoneChecks />}

        {/* Below the fold: SEO content */}
        <Stack gap="xl" mt="xl">
          <Box>
            <Title order={2} size="h3" mb="sm">{tSEO("whatIsTitle")}</Title>
            <Text c="dimmed">
              {tSEO("whatIsAnswer")}
            </Text>
            <Text c="dimmed" mt="sm">
              {tSEO("whatIsAnswer2")}
            </Text>
          </Box>

          <Box>
            <Title order={2} size="h3" mb="sm">{tSEO("howToFindTitle")}</Title>
            <Text c="dimmed">
              {tSEO("howToFindIntro")}
            </Text>
            <Stack gap="sm" mt="sm">
              <Text c="dimmed"><strong>{tSEO("veinTest")}</strong></Text>
              <Text c="dimmed"><strong>{tSEO("jewelryTest")}</strong></Text>
              <Text c="dimmed"><strong>{tSEO("sunReaction")}</strong></Text>
              <Text c="dimmed"><strong>{tSEO("colorPreference")}</strong></Text>
            </Stack>
          </Box>

          {/* Inline CTA */}
          <Box ta="center" py="md">
            <Button
              size="lg"
              onClick={startQuiz}
              rightSection={<IconArrowRight size="1rem" />}
              color="wumpus"
              variant="light"
            >
              {tSEO("takeQuiz")}
            </Button>
          </Box>

          <Box>
            <Title order={2} size="h3" mb="sm">What will I learn from this undertone quiz?</Title>
            <Text c="dimmed">
              After completing the quiz, you'll receive:
            </Text>
            <Stack gap="sm" mt="sm">
              <Text c="dimmed">• Your undertone result: warm, cool, or neutral</Text>
              <Text c="dimmed">• Color recommendations that complement your undertone</Text>
              <Text c="dimmed">• Whether gold or silver jewelry suits you better</Text>
              <Text c="dimmed">• Which color season you're likely to be (Spring, Summer, Autumn, or Winter)</Text>
            </Stack>
          </Box>

          <Box>
            <Title order={2} size="h3" mb="sm">Warm vs Cool vs Neutral Undertones</Title>
            <Text c="dimmed">
              <strong>Warm undertones</strong> have yellow, peachy, or golden hues beneath the skin. Warm undertones look best in earthy colors like orange, yellow, olive green, and cream. Gold jewelry typically complements warm undertones.
            </Text>
            <Text c="dimmed" mt="sm">
              <strong>Cool undertones</strong> have pink, red, or bluish hues beneath the skin. Cool undertones look best in jewel tones like blue, purple, emerald, and pure white. Silver jewelry typically complements cool undertones.
            </Text>
            <Text c="dimmed" mt="sm">
              <strong>Neutral undertones</strong> have a mix of both warm and cool hues, or very little obvious undertone. Neutral undertones can wear both warm and cool colors, and both gold and silver jewelry look good.
            </Text>
          </Box>

          {/* Skin Tone vs Undertone */}
          <Box>
            <Title order={2} size="h3" mb="sm">What Is My Skin Tone vs Undertone?</Title>
            <Text c="dimmed">
              People often confuse skin tone with undertone, but they're different things:
            </Text>
            <Text c="dimmed" mt="sm">
              <strong>Skin tone</strong> is how light or dark your skin appears on the surface—fair, light, medium, tan, or deep. Your skin tone can change with sun exposure, seasons, and age.
            </Text>
            <Text c="dimmed" mt="sm">
              <strong>Undertone</strong> is the subtle color beneath your skin's surface that stays constant throughout your life. Two people can have the same skin tone but completely different undertones.
            </Text>
            <Text c="dimmed" mt="sm">
              This is why some people with fair skin look great in warm coral while others look better in cool pink—it's not about how light or dark your skin is, it's about the underlying warmth or coolness.
            </Text>
          </Box>

          {/* Common Mistakes */}
          <Box>
            <Title order={2} size="h3" mb="sm">Common Mistakes When Determining Undertone</Title>
            <Stack gap="sm" mt="sm">
              <Text c="dimmed">
                <strong>Mistake #1: Testing in artificial light.</strong> Always check your undertone in natural daylight. Fluorescent lights add a cool cast, and warm bulbs add yellow—both will skew your results.
              </Text>
              <Text c="dimmed">
                <strong>Mistake #2: Confusing surface redness with undertone.</strong> Rosacea, acne, or temporary flushing doesn't mean you have cool undertones. Focus on your jawline, neck, and inner wrist where skin is less reactive.
              </Text>
              <Text c="dimmed">
                <strong>Mistake #3: Assuming dark skin = warm undertone.</strong> Cool undertones exist across all skin tones. Many people with deep skin tones have cool or neutral undertones.
              </Text>
              <Text c="dimmed">
                <strong>Mistake #4: Only using one test.</strong> The vein test alone isn't definitive. This quiz uses multiple methods to give you a more accurate result.
              </Text>
            </Stack>
          </Box>

          {/* Inline CTA */}
          <Box ta="center" py="md">
            <Button
              size="lg"
              onClick={startQuiz}
              rightSection={<IconArrowRight size="1rem" />}
              color="wumpus"
              variant="light"
            >
              Find My Undertone Now
            </Button>
          </Box>

          {/* What to do after */}
          <Box>
            <Title order={2} size="h3" mb="sm">What to Do After Finding Your Undertone</Title>
            <Text c="dimmed">
              Once you know your undertone, you can take your color knowledge further:
            </Text>
            <Stack gap="sm" mt="sm">
              <Text c="dimmed">
                <strong>Find your color season:</strong> Your undertone determines whether you're a warm season (Spring/Autumn) or cool season (Summer/Winter). Take our <a href="/color-analysis-quiz" style={{ color: "#12B886" }}>color analysis quiz</a> to find your specific season.
              </Text>
              <Text c="dimmed">
                <strong>Get precise with AI:</strong> For photo-based suggestions, our <a href="/ai-color-analysis" style={{ color: "#12B886" }}>AI color analysis</a> uses your photo to suggest a season and a coordinated color palette.
              </Text>
              <Text c="dimmed">
                <strong>Build your palette:</strong> Once you know your season, you can compare suggested colors with what you already enjoy wearing—making shopping and getting dressed so much easier.
              </Text>
            </Stack>
          </Box>

          <ColorNextSteps source="undertone" />

          {/* Related Tools */}
          <Box>
            <Title order={2} size="h3" mb="sm">More Color & Style Tools</Title>
            <Stack gap="sm">
              <Text c="dimmed">
                <strong><a href="/skin-tone-test" style={{ color: "#12B886" }}>Skin Tone Test</a>:</strong> Learn the difference between undertone and overtone, plus get detailed tips for finding your true skin undertone at home.
              </Text>
              <Text c="dimmed">
                <strong><a href="/best-colors-for-skin-tone" style={{ color: "#12B886" }}>Best Colors for Your Skin Tone</a>:</strong> See specific color recommendations for warm, cool, and neutral undertones—from clothing to makeup.
              </Text>
              <Text c="dimmed">
                <strong><a href="/gold-or-silver-jewelry-test" style={{ color: "#12B886" }}>Gold or Silver Jewelry Test</a>:</strong> Find out whether gold or silver jewelry complements your undertone better.
              </Text>
              <Text c="dimmed">
                <strong><a href="/style-personality-quiz" style={{ color: "#12B886" }}>Style Personality Quiz</a>:</strong> Discover your fashion archetype and get personalized wardrobe recommendations.
              </Text>
            </Stack>
          </Box>
        </Stack>
      </Container>

      {/* Sticky bottom CTA for mobile */}
      {showStickyButton && (
        <Box
          hiddenFrom="md"
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "0.75rem 1rem",
            background: "white",
            borderTop: "1px solid #eee",
            zIndex: 100,
          }}
        >
          <Button
            size="md"
            onClick={startQuiz}
            rightSection={<IconArrowRight size="1rem" />}
            color="wumpus"
            fullWidth
          >
            {t("hero.button")}
          </Button>
        </Box>
      )}
    </>
  );
}
